import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail, Mic, Newspaper, Play } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import SocialLinks from "../components/site/SocialLinks";
import { MARKETING_EMAIL } from "../content/social";
import { formatMediaDate, localized, useMediaItems } from "../lib/media";

const FORMAT_ICONS = { article: Newspaper, video: Play, podcast: Mic, radio: Mic };

// Where a card goes and what its button says.
const cardTarget = (item, p) => {
  if (item.kind === "post") return { to: `/media/${item.slug}`, label: p.readPost };
  const label = item.format === "video" ? p.watch : item.format === "podcast" || item.format === "radio" ? p.listen : p.read;
  return { href: item.url, label };
};

const CardShell = ({ item, target, className, children, testId }) => {
  const track = `media-${item.kind}-${item.id}`;
  if (target.to) {
    return (
      <Link to={target.to} className={className} data-testid={testId} data-track={track}>
        {children}
      </Link>
    );
  }
  return (
    <a href={target.href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId} data-track={track}>
      {children}
    </a>
  );
};

const Eyebrow = ({ item, p, lang }) => {
  const Icon = item.kind === "press" ? FORMAT_ICONS[item.format] || Newspaper : null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]">
      {Icon && <Icon size={13} className="shrink-0" aria-hidden />}
      <span>{item.kind === "press" ? item.outlet : p.fromMise}</span>
      <span className="text-white/35">· {formatMediaDate(item.date, lang)}</span>
    </p>
  );
};

const FeaturedCard = ({ item, p, lang }) => {
  const target = cardTarget(item, p);
  const Arrow = target.to ? ArrowRight : ArrowUpRight;
  return (
    <CardShell
      item={item}
      target={target}
      testId="media-featured"
      className="group relative block overflow-hidden rounded-[1.6rem] bg-gradient-to-br from-[#E8490F]/70 via-[#444444]/50 to-[#242424] p-px transition-shadow duration-300 hover:shadow-[0_30px_70px_-30px_rgba(232,73,15,0.55)]"
    >
      <div className="relative grid h-full gap-8 overflow-hidden rounded-[1.55rem] bg-gradient-to-br from-[#2b1810] via-[#1c1c1c] to-[#171717] p-8 md:p-10 lg:grid-cols-12">
        <div className={item.image ? "lg:col-span-7" : "lg:col-span-12 max-w-4xl"}>
          <p className="mb-5 inline-flex rounded-full border border-[#E8490F]/40 bg-[#E8490F]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E8490F]">
            {p.featured}
          </p>
          <Eyebrow item={item} p={p} lang={lang} />
          <h2 className="mt-4 mise-serif text-3xl md:text-[2.6rem] leading-[1.15] text-white [text-wrap:balance]">
            {localized(item, "title", lang)}
          </h2>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">{localized(item, "summary", lang)}</p>
          <p className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-white group-hover:text-[#E8490F] transition-colors">
            {target.label}
            <Arrow size={16} className="text-[#E8490F] transition-transform group-hover:translate-x-0.5" />
          </p>
        </div>
        {item.image && (
          <div className="lg:col-span-5">
            <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-[#444444] bg-[#0d0d0d]">
              <img src={item.image} alt={item.image_alt || ""} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-left-top" />
            </div>
          </div>
        )}
      </div>
    </CardShell>
  );
};

const MediaCard = ({ item, p, lang, index }) => {
  const target = cardTarget(item, p);
  const Arrow = target.to ? ArrowRight : ArrowUpRight;
  const isPost = item.kind === "post";
  return (
    <CardShell
      item={item}
      target={target}
      testId={`media-card-${index}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border p-6 pl-7 transition-all hover:-translate-y-0.5 ${
        isPost
          ? "border-[#E8490F]/35 bg-gradient-to-br from-[#2b1810] via-[#1c1c1c] to-[#181818] hover:border-[#E8490F]/70"
          : "border-[#444444] bg-[#1a1a1a] hover:border-[#6a6a6a]"
      }`}
    >
      <span
        className={`absolute inset-y-0 left-0 w-1 ${isPost ? "bg-[#E8490F]" : "bg-gradient-to-b from-[#E8490F] to-[#E8490F]/10"}`}
        aria-hidden
      />
      {isPost && item.image && (
        <div className="relative -mx-6 -ml-7 -mt-6 mb-6 aspect-[16/9] overflow-hidden border-b border-[#444444] bg-[#0d0d0d]">
          <img src={item.image} alt={item.image_alt || ""} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-left-top" />
        </div>
      )}
      <Eyebrow item={item} p={p} lang={lang} />
      <h3 className="mt-3 mise-serif text-[1.45rem] leading-snug text-white">{localized(item, "title", lang)}</h3>
      <p className="mt-3 text-sm text-white/65 leading-relaxed">{localized(item, "summary", lang)}</p>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-6">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-white/75 group-hover:text-white">
          {target.label}
          <Arrow size={14} className="text-[#E8490F]" />
        </span>
        {item.tag && (
          <span className="rounded-full border border-[#444444] px-2.5 py-0.5 text-[11px] text-white/50">{p.tags[item.tag]}</span>
        )}
      </div>
    </CardShell>
  );
};

const Media = () => {
  const { t, lang } = useLanguage();
  const p = t.media;
  const { items } = useMediaItems();
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [p.metaTitle]);

  const shown = useMemo(() => (filter === "all" ? items : items.filter((i) => i.kind === filter)), [items, filter]);
  const [featured, ...rest] = shown;

  return (
    <div data-testid="page-media">
      {/* HERO */}
      <section className="border-b border-[#242424]">
        <div className="mise-container pt-6 md:pt-8 pb-12 md:pb-14">
          <p className="mise-eyebrow">{p.eyebrow}</p>
          <h1 className="mt-5 text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            {p.heading} <span className="mise-serif mise-accent">{p.headingItalic}</span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{p.intro}</p>

          <div className="mt-9 inline-flex flex-wrap rounded-full border border-[#444444] p-1 text-sm" role="tablist" aria-label={p.eyebrow}>
            {["all", "press", "post"].map((key) => {
              const active = filter === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setFilter(key)}
                  data-testid={`media-filter-${key}`}
                  data-track={`media-filter-${key}`}
                  className={`rounded-full px-4 py-1.5 transition-colors ${
                    active ? "bg-[#E8490F] text-white" : "text-white/70 hover:text-white"
                  }`}
                >
                  {p.filters[key]}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ITEMS: the newest one large, the rest in a grid */}
      <section className="py-12 md:py-16">
        <div className="mise-container">
          {!featured ? (
            <p className="text-white/60" data-testid="media-empty">{p.empty}</p>
          ) : (
            <>
              <FeaturedCard item={featured} p={p} lang={lang} />
              {rest.length > 0 && (
                <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="media-grid">
                  {rest.map((item, i) => (
                    <MediaCard key={item.id} item={item} p={p} lang={lang} index={i} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* PRESS ENQUIRIES */}
      <section className="relative overflow-hidden border-t border-[#242424] py-16 md:py-20" data-testid="media-press-contact">
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[360px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E8490F]/10 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <p className="mise-eyebrow">{p.pressKit.eyebrow}</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">{p.pressKit.heading}</h2>
            <p className="mt-4 max-w-xl text-white/70 leading-relaxed">{p.pressKit.text}</p>
            <a
              href={`mailto:${MARKETING_EMAIL}`}
              className="mt-7 mise-pill-primary"
              data-testid="media-press-email"
              data-track="media-press-email"
            >
              <Mail size={16} />
              {MARKETING_EMAIL}
            </a>
          </div>
          <div className="lg:col-span-5">
            <div className="mise-card p-7 hover:!translate-y-0">
              <p className="mise-eyebrow mb-4">{p.pressKit.follow}</p>
              <SocialLinks withHandles className="space-y-3 text-white/75" testIdPrefix="media-social" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Media;
